import type { ReactNode } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Briefcase,
  GraduationCap,
  Code,
  Globe,
  FileText,
  Loader2,
  Calendar,
  Route as RouteIcon,
  BookOpen,
  Award,
  ShieldCheck,
  Clock,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from "@/lib/api-client";

// @ts-ignore
export const Route = createFileRoute("/p/$slug")({
  head: () => ({
    meta: [
      { title: "Profile — Dev-Hub" },
      {
        name: "description",
        content:
          "View a Dev-Hub member profile showcasing completed courses, certificates, and professional background.",
      },
      { property: "og:title", content: "Profile — Dev-Hub" },
      {
        property: "og:description",
        content:
          "View a Dev-Hub member profile showcasing completed courses, certificates, and professional background.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    // No canonical — this is a per-record page containing personal data; a canonical
    // on a noindex page is a mixed signal.
  }),
  component: PublicPortfolioPage,
});

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "";

// Backend returns image paths relative to the API host (e.g. "/uploads/images/…"). Prefix them
// with the API host so they don't resolve against the frontend origin (which 404s).
const apiHost = (API_BASE_URL ?? "").replace(/\/api\/?$/, "");
const toUrl = (path?: string | null): string | undefined =>
  !path
    ? undefined
    : /^https?:\/\//i.test(path)
      ? path
      : `${apiHost}${path.startsWith("/") ? "" : "/"}${path}`;

function SectionTitle({ icon: Icon, children }: { icon: any; children: ReactNode }) {
  return (
    <h2 className="text-xl font-bold mb-4 flex items-center gap-2.5">
      <span className="h-6 w-1 rounded-full bg-accent" />
      <Icon className="h-5 w-5 text-primary" /> {children}
    </h2>
  );
}

function PublicPortfolioPage() {
  const { slug } = useParams({ from: "/p/$slug" }) as any;

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-profile", slug],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/public/profile/${slug}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Profile not found");
      }
      return res.json();
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data || !data.profile) {
    // Known soft-404: this branch returns HTTP 200 because all data loading is
    // client-side (useQuery). The route's `noindex, nofollow` head meta prevents
    // indexing, but the status code remains 200. Fixing this requires moving the
    // fetch into a route `loader` so the server can emit a real 404 status — that
    // is a data-loading architecture change outside the scope of this SEO pass.
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-muted/20 text-center p-6">
        <h1 className="text-3xl font-bold mb-2">Profile Not Found</h1>
        <p className="text-muted-foreground max-w-md">
          This profile either does not exist, or the user has chosen to keep it private.
        </p>
      </div>
    );
  }

  const { profile, cv, tracks = [], courses = [], certificates = [] } = data;

  return (
    <div className="min-h-screen bg-muted/10 font-sans pb-20">
      {/* Brand gradient hero banner */}
      <div
        className="relative h-52 w-full overflow-hidden"
        style={{ backgroundImage: "var(--gradient-brand)" }}
      >
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl"
          style={{ background: "color-mix(in oklab, var(--accent) 22%, transparent)" }}
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-28">
        {/* Profile Card */}
        <div
          className="bg-card rounded-2xl border p-6 sm:p-10 relative mb-8"
          style={{ boxShadow: "var(--shadow-card)" }}
        >
          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-end -mt-16 sm:-mt-24 mb-6">
            <Avatar className="h-32 w-32 border-4 border-accent/70 bg-card shadow-lg">
              <AvatarImage src={toUrl(profile.avatar_url)} />
              <AvatarFallback className="text-4xl bg-primary/10 text-primary">
                {profile.full_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 pb-2">
              <h1 className="text-3xl font-bold">{profile.full_name}</h1>
              {profile.study && (
                <p className="text-lg text-muted-foreground font-medium mt-1">{profile.study}</p>
              )}
            </div>
            {profile.has_cv_file && (
              <Button
                asChild
                className="shrink-0 bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <a href={`${API_BASE_URL}/public/cv/${slug}`} target="_blank" rel="noreferrer">
                  <FileText className="mr-2 h-4 w-4" />
                  Download CV
                </a>
              </Button>
            )}
          </div>

          {(profile.email || profile.batch) && (
            <div className="flex flex-wrap gap-y-3 gap-x-6 text-sm text-muted-foreground mt-6 border-t pt-6">
              {profile.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <a
                    href={`mailto:${profile.email}`}
                    className="hover:text-primary transition-colors"
                  >
                    {profile.email}
                  </a>
                </div>
              )}
              {profile.batch && (
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-primary" />
                  Batch {profile.batch}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="md:col-span-2 space-y-8">
            {profile.bio && (
              <section>
                <SectionTitle icon={Globe}>About</SectionTitle>
                <div className="bg-card p-6 rounded-xl border shadow-sm leading-relaxed text-card-foreground/90 whitespace-pre-wrap">
                  {profile.bio}
                </div>
              </section>
            )}

            {/* Certificates */}
            {certificates.length > 0 && (
              <section>
                <SectionTitle icon={Award}>Certificates</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {certificates.map((cert: any, i: number) => (
                    <a
                      key={i}
                      href={`/verify/${cert.cert_code}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group block rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-md hover:border-accent/50 transition-all"
                    >
                      <div className="h-1.5" style={{ backgroundImage: "var(--gradient-warm)" }} />
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="h-10 w-10 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
                            <Award className="h-5 w-5 text-accent" />
                          </div>
                          <Badge variant="outline" className="capitalize text-[10px]">
                            {cert.type}
                          </Badge>
                        </div>
                        <h3 className="font-semibold mt-3 leading-snug line-clamp-2">
                          {cert.title}
                        </h3>
                        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {formatDate(cert.issued_at)}
                          </span>
                          <span className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                            Verify <ExternalLink className="h-3 w-3" />
                          </span>
                        </div>
                        {cert.cert_code && (
                          <div className="mt-2 flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
                            <ShieldCheck className="h-3 w-3" /> {cert.cert_code}
                          </div>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Completed Courses */}
            {courses.length > 0 && (
              <section>
                <SectionTitle icon={BookOpen}>Completed Courses</SectionTitle>
                <div className="space-y-3">
                  {courses.map((course: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 bg-card p-4 rounded-xl border shadow-sm"
                    >
                      {course.thumbnail_url ? (
                        <img
                          src={toUrl(course.thumbnail_url)}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover shrink-0 border"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <BookOpen className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold truncate">{course.name}</h3>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          {course.hours != null && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {course.hours}h
                            </span>
                          )}
                          {course.completed_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" /> {formatDate(course.completed_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-success-muted text-success border-success/20 shrink-0">
                        Completed
                      </Badge>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cv?.experience && cv.experience.length > 0 && (
              <section>
                <SectionTitle icon={Briefcase}>Experience</SectionTitle>
                <div className="space-y-4">
                  {cv.experience.map((exp: any, i: number) => (
                    <div key={i} className="bg-card p-6 rounded-xl border shadow-sm">
                      <h3 className="font-semibold text-lg">{exp.title}</h3>
                      <div className="text-muted-foreground text-sm flex items-center gap-2 mb-3">
                        <span className="font-medium text-foreground/80">{exp.company}</span>
                        <span>•</span>
                        <span>{exp.duration}</span>
                      </div>
                      <p className="text-sm leading-relaxed">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cv?.projects && cv.projects.length > 0 && (
              <section>
                <SectionTitle icon={Code}>Projects</SectionTitle>
                <div className="space-y-4">
                  {cv.projects.map((proj: any, i: number) => (
                    <div key={i} className="bg-card p-6 rounded-xl border shadow-sm">
                      <h3 className="font-semibold text-lg">{proj.name}</h3>
                      <div className="text-muted-foreground text-sm flex items-center gap-2 mb-3">
                        <span>{proj.period}</span>
                      </div>
                      <p className="text-sm leading-relaxed mb-4">{proj.description}</p>
                      {proj.technologies && (
                        <div className="flex flex-wrap gap-2">
                          {proj.technologies.split(",").map((tech: string, j: number) => (
                            <Badge key={j} variant="secondary" className="font-normal">
                              {tech.trim()}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Learning Tracks */}
            {tracks.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <RouteIcon className="h-5 w-5 text-primary" /> Learning Tracks
                </h2>
                <div className="space-y-3">
                  {tracks.map((track: any, i: number) => (
                    <div key={i} className="bg-card p-4 rounded-xl border shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <RouteIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-sm truncate">{track.name}</h3>
                          {track.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {track.description}
                            </p>
                          )}
                          <p className="text-[11px] text-muted-foreground mt-1.5 font-medium">
                            {track.course_count} {track.course_count === 1 ? "course" : "courses"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cv?.skills && cv.skills.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Skills</h2>
                <div className="bg-card p-5 rounded-xl border shadow-sm">
                  <div className="flex flex-wrap gap-2">
                    {cv.skills.map((skill: any, i: number) => (
                      <Badge key={i} variant="outline" className="bg-muted/50">
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {cv?.education && cv.education.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Education</h2>
                <div className="space-y-4">
                  {cv.education.map((edu: any, i: number) => (
                    <div key={i} className="bg-card p-5 rounded-xl border shadow-sm">
                      <h3 className="font-semibold">{edu.degree}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{edu.institution}</p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {edu.year}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {cv?.languages && cv.languages.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-4">Languages</h2>
                <div className="bg-card p-5 rounded-xl border shadow-sm">
                  <ul className="space-y-3">
                    {cv.languages.map((lang: any, i: number) => (
                      <li key={i} className="flex justify-between items-center text-sm">
                        <span className="font-medium">{lang.language}</span>
                        <span className="text-muted-foreground">{lang.proficiency}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
